import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import toast from 'react-hot-toast';
import { THEME_MESSAGES } from 'utils/constants';
import { getPaymentDiscrepancyLogs } from 'api/admin/paymentDiscrepancyLogs/paymentDiscrepancyLogs';
import { ApiError } from 'types/api';

export type PaymentDiscrepancyLogSource = 'cron' | 'stripe_webhook';

export type PaymentDiscrepancyLogType = 'balance' | 'tip';

export interface TipDiscrepancyDetails {
  expectedTipAmountInCents: number;
  storedTipAmountInCents: number;
  tipOption: string | null;
}

export interface PaymentDiscrepancyLogListItem {
  id: number;
  createdAt: string;
  source: PaymentDiscrepancyLogSource;
  serviceOrderId: number;
  netOrderTotalInCents: number;
  paidAmountInCents: number;
  balanceDueInCents: number;
  discrepancyType: PaymentDiscrepancyLogType | null;
  details: TipDiscrepancyDetails | null;
  resolvedAt: string | null;
}

interface GetPaymentDiscrepancyLogsOutput {
  success: boolean;
  total: number;
  paymentDiscrepancyLogs: PaymentDiscrepancyLogListItem[];
}

interface PaymentDiscrepancyLogsResult {
  logs: PaymentDiscrepancyLogListItem[];
  total: number;
}

export const usePaymentDiscrepancyLogs = (pageNumber: number, limit: number) => {
  return useQuery<unknown, ApiError, PaymentDiscrepancyLogsResult>({
    queryKey: ['paymentDiscrepancyLogs', { pageNumber, limit }],
    queryFn: async () => {
      const { data } = (await getPaymentDiscrepancyLogs(
        pageNumber,
        limit,
      )) as AxiosResponse<GetPaymentDiscrepancyLogsOutput>;

      return { logs: data.paymentDiscrepancyLogs, total: data.total };
    },
    onError: (error) => {
      Sentry.captureException(error);
      toast.error(error?.response?.data?.error ?? THEME_MESSAGES.somethingWrong, {
        duration: 4000,
      });
    },
    keepPreviousData: true,
  });
};
